import _omit from "lodash/omit";
import Validator from 'validatorjs';
import moment from 'moment';
import { SUPPRESSION_TYPES, SUPPRESSION_TYPE_LABELS } from '@kentik/ui-shared/alerting/constants';
import alarmSuppressionFields from 'app/forms/config/synthetics/fields/alarmSuppressionFields';
import { createContextFilterFields } from 'app/forms/config/contextFilters/contextFilters';
import { getUnknownFormDimensions } from 'app/stores/suppressions/suppressionDimensions';
const {
  startTime: startTimeField,
  expirationTime: endTimeField,
  neverExpire
} = alarmSuppressionFields;

/** The subset of form values the custom validators below read off the validator input. */

const register = (name, rule, message) => Validator.register(name, rule, message);
register('noDuplicateDimensions',
// This validation only applies to condition types
function noDuplicateDimensions(filters) {
  const {
    type
  } = this.validator.input;
  const conditionTypes = [SUPPRESSION_TYPES.CONDITION, SUPPRESSION_TYPES.METRIC, SUPPRESSION_TYPES.NMS];
  if (!type || !conditionTypes.includes(type)) {
    return true;
  }
  const dimensions = filters.map(({
    filterField
  }) => filterField);
  return dimensions.length === new Set(dimensions).size;
}, 'Each condition dimension can be used only once in an Alert Suppression');
register('knownDimension', function knownDimension(filterField) {
  return !getUnknownFormDimensions(this.validator.input).includes(filterField);
}, 'This dimension is unknown and cannot be matched');

/**
 * Owned by the `conditionsGroup` field, but satisfiable by any criteria field. Exported so the
 * Matching criteria section can pick it out of that field's errors, which also aggregate every
 * descendant's messages for the form-level summary.
 */
export const SUPPRESSION_SCOPE_MESSAGE = 'Select a policy, application, severity, or add a condition';
register('suppressionScope', function suppressionScope() {
  const {
    rule_ids,
    severities,
    applications,
    context_filter,
    conditionsGroup
  } = this.validator.input;
  return Boolean(rule_ids?.length || severities?.length || applications?.length || context_filter?.conjunctions?.length || conditionsGroup?.some(({
    filters
  }) => filters?.length));
}, SUPPRESSION_SCOPE_MESSAGE);
const isConditionType = form => [SUPPRESSION_TYPES.CONDITION, SUPPRESSION_TYPES.NMS, SUPPRESSION_TYPES.METRIC].includes(form.getValue('type'));
export const options = {
  name: 'Alert Suppression',
  hideErrorsWhileValidating: true
};
export const getConditionsGroupForType = type => {
  if (type === SUPPRESSION_TYPES.NONE) {
    return [];
  }
  if (type === SUPPRESSION_TYPES.METRIC) {
    // Not a user-facing condition: kmetrics suppressions carry the measurement as a filter.
    return [{
      connector: 'partial',
      filters: [{
        filterField: 'km_measurement_name'
      }]
    }];
  }
  if ([SUPPRESSION_TYPES.NMS, SUPPRESSION_TYPES.CONDITION].includes(type)) {
    // Empty: conditions are optional now that policies, applications and severities can scope a
    // suppression on their own, and a preselected row would silently satisfy the scope rule.
    return [{
      connector: 'partial',
      filters: []
    }];
  }
  return [];
};
const SCOPE_FIELD_NAME = 'conditionsGroup';
const CRITERIA_FIELD_NAMES = [SCOPE_FIELD_NAME, 'rule_ids', 'applications', 'severities', 'context_filter.conjunctions'];
export const hasContextFilterConditions = form => (form.getValue('context_filter.conjunctions') || []).length > 0;
export const hasCriteriaScopeError = form => {
  const criteriaFields = CRITERIA_FIELD_NAMES.map(name => form.getField(name));
  const touched = criteriaFields.some(field => field?.pristine === false);
  const validating = criteriaFields.some(field => field?.validating);
  const scopeField = form.getField(SCOPE_FIELD_NAME);
  return touched && !validating && Boolean(scopeField?.errors.includes(SUPPRESSION_SCOPE_MESSAGE));
};
export const FILTER_OPERATOR_OPTIONS = [{
  label: 'equals',
  value: 'equals'
}, {
  label: 'does not equal',
  value: 'not_equals'
}, {
  label: 'starts with',
  value: 'starts_with'
}, {
  label: 'does not start with',
  value: 'not_starts_with'
}, {
  label: 'ends with',
  value: 'ends_with'
}, {
  label: 'does not end with',
  value: 'not_ends_with'
}, {
  label: 'contains',
  value: 'contains'
}, {
  label: 'does not contain',
  value: 'not_contains'
}, {
  label: 'is one of',
  value: 'in'
}, {
  label: 'is not one of',
  value: 'not_in'
}, {
  label: 'matches regex',
  value: 'matches_regex'
}, {
  label: 'does not match regex',
  value: 'not_matches_regex'
}, {
  label: 'is empty',
  value: 'empty'
}, {
  label: 'is present',
  value: 'not_empty'
}, {
  label: 'matches any value',
  value: 'any'
}];

// Used when building the filter payload to translate the selected value to { key, not } format expected by the API
export const CONDITION_OPERATOR_MAP = {
  equals: {
    key: 'equals',
    not: false
  },
  not_equals: {
    key: 'equals',
    not: true
  },
  starts_with: {
    key: 'starts_with',
    not: false
  },
  not_starts_with: {
    key: 'starts_with',
    not: true
  },
  ends_with: {
    key: 'ends_with',
    not: false
  },
  not_ends_with: {
    key: 'ends_with',
    not: true
  },
  contains: {
    key: 'contains',
    not: false
  },
  not_contains: {
    key: 'contains',
    not: true
  },
  in: {
    key: 'in',
    not: false
  },
  not_in: {
    key: 'in',
    not: true
  },
  matches_regex: {
    key: 'matches_regex',
    not: false
  },
  not_matches_regex: {
    key: 'matches_regex',
    not: true
  },
  empty: {
    key: 'empty',
    not: false
  },
  not_empty: {
    key: 'empty',
    not: true
  },
  any: {
    key: 'any',
    not: false
  }
};
const BOOLEAN_OPERATORS = ['empty', 'any'];
const MULTI_VALUE_OPERATORS = ['in', 'not_in'];

/**
 * Operators carrying no value of their own; the API expects a literal `true`. Exposed as strings so
 * they compare cleanly against untyped form values, while `satisfies` above keeps the members
 * validated against the operator union.
 */
export const BOOLEAN_CONDITION_OPERATORS = BOOLEAN_OPERATORS;

/** Operators whose value is a list rather than a scalar. */
export const MULTI_VALUE_CONDITION_OPERATORS = MULTI_VALUE_OPERATORS;
export const isBooleanConditionOperator = operator => BOOLEAN_CONDITION_OPERATORS.includes(CONDITION_OPERATOR_MAP[operator]?.key ?? operator);

// For loading an existing suppression, so that the correct operator shows as selected in the UI when a suppression is being edited
export const getFilterOperator = value => {
  const not = value.not === true;
  if ('equals' in value) {
    return not ? 'not_equals' : 'equals';
  }
  if ('starts_with' in value) {
    return not ? 'not_starts_with' : 'starts_with';
  }
  if ('ends_with' in value) {
    return not ? 'not_ends_with' : 'ends_with';
  }
  if ('contains' in value) {
    return not ? 'not_contains' : 'contains';
  }
  if ('in' in value) {
    return not ? 'not_in' : 'in';
  }
  if ('matches_regex' in value) {
    return not ? 'not_matches_regex' : 'matches_regex';
  }
  if ('empty' in value) {
    return not ? 'not_empty' : 'empty';
  }
  if ('any' in value) {
    return 'any';
  }
  return 'equals';
};
export const buildKeyValueFilters = filters => filters.map(({
  filterField,
  filterOperator = 'equals',
  filterValue
}) => {
  const {
    key,
    not
  } = CONDITION_OPERATOR_MAP[filterOperator] ?? CONDITION_OPERATOR_MAP.equals;
  const isBooleanOperator = BOOLEAN_CONDITION_OPERATORS.includes(key);
  const isMultiValueOperator = key === 'in';
  if (!isBooleanOperator && filterValue === undefined) {
    throw new Error(`filterValue is required for "${filterOperator}`);
  }
  if (isMultiValueOperator && (!Array.isArray(filterValue) || filterValue.length === 0)) {
    throw new Error(`filterValue must contain at least one value for "${filterOperator}`);
  }
  let serializedValue = filterValue;
  if (isBooleanOperator) {
    serializedValue = true;
  } else if (isMultiValueOperator) {
    serializedValue = {
      values: filterValue.map(String)
    };
  }
  return {
    key: {
      equals: filterField
    },
    value: {
      ...(not && {
        not: true
      }),
      [key]: serializedValue
    }
  };
});
const EDITABLE_FILTER_OPERATORS = new Set(Object.values(CONDITION_OPERATOR_MAP).map(({
  key
}) => key));
export const isEditableKeyValueFilter = ({
  key,
  value
}) => Boolean(key?.equals && value && Object.keys(value).some(valueKey => valueKey !== 'not' && EDITABLE_FILTER_OPERATORS.has(valueKey)));
export const replaceEditableKeyValueFilters = (existing, editableFilters, strict) => ({
  // `filter` is the deprecated map form of `filters`, and the API rejects a payload carrying both.
  // It is echoed back on read, so it has to be dropped here rather than forwarded.
  ..._omit(existing, 'filter'),
  filters: [...(existing?.filters || []).filter(filter => !isEditableKeyValueFilter(filter)), ...editableFilters],
  strict
});
export const buildMultiValueKeyFilter = (filterFields, filterValues) => ({
  filters: [{
    key: Array.isArray(filterFields) ? {
      in: {
        values: filterFields
      }
    } : {
      equals: filterFields
    },
    value: {
      not: false,
      in: {
        values: filterValues.map(String)
      }
    }
  }]
});

// shitty inline until form goes ts

export const fields = {
  type: {
    label: 'Alert Type',
    defaultValue: SUPPRESSION_TYPES.NONE,
    options: [SUPPRESSION_TYPES.NONE, SUPPRESSION_TYPES.CONDITION, SUPPRESSION_TYPES.NMS, SUPPRESSION_TYPES.METRIC, SUPPRESSION_TYPES.SYNTH, SUPPRESSION_TYPES.AGENT].map(value => ({
      value,
      label: SUPPRESSION_TYPE_LABELS[value]
    }))
  },
  start_time: {
    ...startTimeField,
    defaultValue: () => moment().add(20, 'minutes')
  },
  end_time: {
    ...endTimeField,
    rules: 'required|date',
    skipValidationIf: ({
      form
    }) => form.getValue('neverExpire'),
    messages: {
      required: 'An end date is required unless Never Expire is selected'
    }
  },
  neverExpire,
  'uiProperties.startAsSoonAsPossible': {
    label: 'Start as soon as possible',
    defaultValue: true
  },
  comment: {
    label: 'Comment',
    placeholder: 'Enter comment...'
  },
  display_name: {
    label: 'Name',
    placeholder: 'Enter name...'
  },
  ...createContextFilterFields('context_filter'),
  conditionsGroup: {
    isComplexArray: true,
    label: 'Condition Group',
    rules: ['suppressionScope'],
    skipValidationIf: ({
      form
    }) => [SUPPRESSION_TYPES.SYNTH, SUPPRESSION_TYPES.AGENT].includes(form.getValue('type'))
  },
  'conditionsGroup[].connector': {
    defaultValue: 'partial',
    options: [{
      label: 'Strict Dimension Match',
      value: 'full'
    }, {
      label: 'Partial Dimension Match',
      value: 'partial'
    }]
  },
  'conditionsGroup[].filters': {
    defaultValue: [],
    label: 'Condition Group',
    isComplexArray: true,
    rules: ['noDuplicateDimensions'],
    skipValidationIf: ({
      form
    }) => !isConditionType(form)
  },
  'conditionsGroup[].filters[].filterField': {
    rules: 'required|noConflictingUniques|knownDimension',
    skipValidationIf: ({
      form
    }) => !isConditionType(form),
    // A row only exists because it was added, so an incomplete one reports itself straight away.
    showPristineErrors: true,
    label: 'field'
  },
  'conditionsGroup[].filters[].filterOperator': {
    defaultValue: 'equals',
    options: FILTER_OPERATOR_OPTIONS
  },
  'conditionsGroup[].filters[].filterValue': {
    rules: 'required',
    skipValidationIf: ({
      form,
      adjacentFields
    }) => !isConditionType(form) || isBooleanConditionOperator(adjacentFields.filterOperator.getValue()),
    showPristineErrors: true,
    placeholder: 'Enter value...',
    label: 'value',
    messages: {
      required: 'A condition value is required'
    }
  },
  rule_ids: {
    defaultValue: [],
    label: 'Policies'
  },
  severities: {
    defaultValue: [],
    label: 'Severities'
  },
  applications: {
    defaultValue: [],
    label: 'Applications'
  },
  testIds: {
    defaultValue: [],
    rules: 'required',
    skipValidationIf: ({
      form
    }) => form.getValue('type') !== SUPPRESSION_TYPES.SYNTH,
    messages: {
      required: 'Please select one or more tests'
    }
  },
  agentIds: {
    defaultValue: [],
    rules: 'required',
    skipValidationIf: ({
      form
    }) => form.getValue('type') !== SUPPRESSION_TYPES.AGENT,
    messages: {
      required: 'Please select one or more agents'
    }
  },
  measurement: {
    label: 'Measurement',
    rules: [{
      required_if: ['type', SUPPRESSION_TYPES.NMS]
    }],
    skipValidationIf: ({
      form
    }) => form.getValue('type') !== SUPPRESSION_TYPES.NMS || !(form.getValue('conditionsGroup') || []).some(({
      filters
    }) => filters?.length),
    showPristineErrors: true,
    messages: {
      required_if: 'Please select a measurement'
    }
  }
};
export const getSuppressionFields = (configurationDisabled, embedded, requireDisplayName = false) => Object.fromEntries(Object.entries(fields).map(([name, config]) => [name, {
  ...config,
  ...(name === 'display_name' && requireDisplayName ? {
    rules: 'required'
  } : {}),
  ...(embedded && name === 'testIds' ? {
    rules: 'required',
    messages: {
      required: 'Please select one or more tests'
    }
  } : {}),
  skipValidationIf: field => name !== 'end_time' && name !== 'neverExpire' && configurationDisabled() || (config.skipValidationIf?.(field) ?? false)
}]));